import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Grid, Card, CardContent, Typography, Box, Stack, Chip,
  CircularProgress, Paper, Divider, List, ListItem, ListItemText, Button,
} from '@mui/material';
import {
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  Inbox as UnmatchedIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import fundedAPI from '../../api/funded';
import FundedNav from './FundedNav';
import SimpleBarChart from '../../components/funded/SimpleBarChart';
import { currency, fmtDateTime } from '../../components/funded/format';

const StatCard = ({ icon, label, value, color, onClick, hint }) => (
  <Card
    elevation={0}
    onClick={onClick}
    sx={{
      border: '1px solid', borderColor: 'divider', borderRadius: 2, cursor: onClick ? 'pointer' : 'default',
      transition: 'all .15s', '&:hover': onClick ? { borderColor: color, boxShadow: 2 } : {},
    }}
  >
    <CardContent>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box sx={{ width: 44, height: 44, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${color}15`, color }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={800} lineHeight={1.1}>{value}</Typography>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
        </Box>
      </Stack>
      {hint && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{hint}</Typography>}
    </CardContent>
  </Card>
);

export default function FundedDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([fundedAPI.getStats(), fundedAPI.getCharts()]);
        setStats(s.data.data);
        setCharts(c.data.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <Box display="flex" justifyContent="center" py={10}><CircularProgress /></Box>;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <FundedNav
        title="Funded Book"
        subtitle={stats?.lastImportAt ? `Last import ${fmtDateTime(stats.lastImportAt)}` : 'Historical funded-deal data'}
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard icon={<BusinessIcon />} label="Funded Clients" value={stats?.clientCount ?? 0} color="#2563eb"
            onClick={() => navigate('/sales/funded/clients')} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard icon={<MoneyIcon />} label="Total Commissions" value={currency(stats?.totalCommissions)} color="#059669"
            hint={`${stats?.commissionCount ?? 0} ACH events`} onClick={() => navigate('/sales/funded/ledger')} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard icon={<UnmatchedIcon />} label="Unmatched Deposits" value={stats?.unmatchedCount ?? 0} color="#d97706"
            hint="Awaiting a client match" onClick={() => navigate('/sales/funded/unmatched')} />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>Commissions by Lender</Typography>
            <SimpleBarChart data={charts?.byLender || []} color="#2563eb" />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>Commissions by Year</Typography>
            <SimpleBarChart data={charts?.byYear || []} color="#059669" />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={700}>Recent Activity</Typography>
              <Button size="small" endIcon={<ArrowIcon />} onClick={() => navigate('/sales/funded/unmatched')}>Act</Button>
            </Stack>
            {(!stats?.recentActivity || stats.recentActivity.length === 0) ? (
              <Typography variant="body2" color="text.secondary">No write activity yet. Matching a deposit or adding a note will appear here.</Typography>
            ) : (
              <List dense disablePadding>
                {stats.recentActivity.map((a) => (
                  <ListItem key={a._id} disableGutters sx={{ py: 0.5 }}>
                    <ListItemText
                      primary={<Typography variant="body2">{a.summary}</Typography>}
                      secondary={<Typography variant="caption" color="text.secondary">{a.performedByName || 'System'} · {fmtDateTime(a.createdAt)}</Typography>}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
